/**
 * The plan's chat (docs/plans/chat-on-document-pipeline.md, "The plan's chat"): its history by plan, kept on the
 * server in the plan's conversation; a message sent comes back with the plan as it is now, and, when the request is
 * read again with it, the document the page then follows to its new plan.
 */
import { create } from 'zustand';
import { client, errorText } from '@/lib/api';
import type { plans } from '@/lib/client';
import { useBrowserStore } from './browserStore';
import { usePlansStore } from './plansStore';

interface PlanChatState {
  messagesByPlan: Record<string, plans.PlanChatMessage[]>;
  /** The document a message started reading, by plan: the page follows it to the plan that replaces this one. */
  rereadByPlan: Record<string, string>;
  /** Whether the server keeps this plan's history for the caller: when not (another user's plan chat), the page does. */
  keptByPlan: Record<string, boolean>;
  sending: Record<string, boolean>;
  error: Record<string, string | null>;
  load(planId: string): Promise<void>;
  /** `screen`: the text of what the admin's page shows, read by the chat with the run. */
  send(planId: string, message: string, screen?: string): Promise<boolean>;
  /** The page moved on to the re-read's document or plan: stop following it. */
  followed(planId: string): void;
}

export const usePlanChatStore = create<PlanChatState>((set) => ({
  messagesByPlan: {},
  rereadByPlan: {},
  keptByPlan: {},
  sending: {},
  error: {},

  async load(planId) {
    try {
      const { messages, kept } = await client.plans.getPlanChat(planId);
      set((s) => ({
        // A history the server does not keep is the page's own: a reload of the chat leaves it as it is.
        messagesByPlan: kept ? { ...s.messagesByPlan, [planId]: messages } : s.messagesByPlan,
        keptByPlan: { ...s.keptByPlan, [planId]: kept },
      }));
    } catch (err) {
      set((s) => ({ error: { ...s.error, [planId]: errorText(err) } }));
    }
  },

  async send(planId, message, screen) {
    const text = message.trim();
    if (text === '') return false;
    // The message shows at once; the reply replaces the history with the server's.
    set((s) => ({
      messagesByPlan: { ...s.messagesByPlan, [planId]: [...(s.messagesByPlan[planId] ?? []), { role: 'user', content: text }] },
      sending: { ...s.sending, [planId]: true },
      error: { ...s.error, [planId]: null },
    }));
    try {
      const res = await client.plans.sendPlanChat(planId, { message: text, ...(screen ? { screen } : {}) });
      usePlansStore.getState().applyPlan(res.plan);
      // "Take me there": the page the message asked for, opened in the browser panel.
      if (res.navigate_url !== undefined) {
        const browser = useBrowserStore.getState();
        browser.openPanel();
        browser.setPendingNavigateUrl(res.navigate_url);
      }
      set((s) => ({
        // Not kept on the server: the reply is this exchange alone, after what the page already shows.
        messagesByPlan: { ...s.messagesByPlan, [planId]: res.kept ? res.messages : [...(s.messagesByPlan[planId] ?? []), ...res.messages.slice(-1)] },
        keptByPlan: { ...s.keptByPlan, [planId]: res.kept },
        ...(res.document_id === undefined ? {} : { rereadByPlan: { ...s.rereadByPlan, [planId]: res.document_id } }),
      }));
      return true;
    } catch (err) {
      set((s) => ({ error: { ...s.error, [planId]: errorText(err) } }));
      return false;
    } finally {
      set((s) => ({ sending: { ...s.sending, [planId]: false } }));
    }
  },

  followed(planId) {
    set((s) => ({ rereadByPlan: Object.fromEntries(Object.entries(s.rereadByPlan).filter(([id]) => id !== planId)) }));
  },
}));
